import { eventQBus } from "../types/EventQBus";
import NextBestAction from "../nextBestAction/NextBestAction.svelte";
import NextBestActionDesktop from "../nextBestAction/NextBestActionDesktop.svelte";
import FacetValueTags from "../facetvaluetags/FacetValueTags.svelte";
import Filterbar from "../nextBestAction/Filterbar.svelte";
import FilterBanner from "../filterBanner/FilterBanner.svelte";
import DesktopSorting from "../sorting/DesktopSorting.svelte";
import ConfigurableFacet from "../facet/ConfigurableFacet.svelte";
import FacetGroupDropdown from "../facetGroup/FacetGroupDropdown.svelte";
import type { ConfigurableFilterOptions } from "@otto-ec/heureka-components/filter";
import {
  Assets,
  setActiveConfiguration,
  setOpenFilterIds,
  toggleFilterAccordion,
} from "@otto-ec/heureka-components/filter";
import { getActiveVariation } from "../experiment/Experiments";
import type { Component } from "svelte";
import { mount } from "svelte";
import type { Facet } from "@otto-ec/heureka-components/facet";
import { clear, mapElements, notEmpty } from "../util/Utils";
import { isDesktop, isMobile } from "../util/Breakpoint";
import { CustomRangeInput } from "./slider/CustomRangeInput";
import { events } from "../events";
import FilterHarmonized from "../filter/FilterHarmonized.svelte";
import { FilterTitle } from "./FilterTitle";
import { isActive } from "../toggle/Toggle";
import { NextBestActionComponent, resetNBAScrollTracking } from "../nextBestAction/NextBestActionComponent";
import { refinementBar } from "../filtersheet/Selectors";
import { OcFilterAccordion } from "./OcFilterAccordion";

/*                                                                                                            */
const config: Record<string, ConfigurableFilterOptions> = {
  color: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "grid",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "color",
                titleSize: "100",
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "overflowBox",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "color",
                titleSize: "75",
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "queueBox",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "color",
                titleSize: "75",
              },
            },
          },
        },
      },
    },
  },
  rating: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "radio",
              facetValueLabelConfig: {
                type: "rating",
              },
            },
          },
        },
      },
    },
  },
  singleSelectRange: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "79",
            containsRanges: true,
            facetValueConfig: {
              type: "singleSelectRange",
              facetValueLabelConfig: {
                type: "text",
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "100",
            containsRanges: true,
            facetValueConfig: {
              type: "singleSelectRange",
              facetValueLabelConfig: {
                type: "text",
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "100",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                size: "75",
                fitContent: true,
                showCount: false,
              },
            },
          },
        },
      },
    },
  },
  intervalDeprecated: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "90",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "90",
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "overflowBox",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
    },
  },
  interval: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "90",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "100",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        facetConfig: {
          type: "interval",
          facetValueGroupConfig: {
            type: "grid",
            gridCellMinSize: "100",
            containsRanges: true,
            facetValueConfig: {
              type: "range",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
    },
  },
  switch: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "toggle",
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "toggle",
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
    },
  },
  radio: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "radio",
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
    },
  },
  chip: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "grid",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "queueBox",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
      sheetSecondLevel: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            type: "grid",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "text",
                fitContent: true,
                size: "75",
                showCount: false,
              },
            },
          },
        },
      },
    },
  },
  ottoLogo: {
    filterConfig: {
      default: {
        facetConfig: {
          type: "simple",
          facetValueGroupConfig: {
            facetValueConfig: {
              type: "toggle",
              facetValueLabelConfig: {
                type: "textWithLogo",
                size: "100",
                logo: "otto",
              },
            },
          },
        },
      },
    },
  },
  default: {
    filterConfig: {
      default: {
        facetConfig: {
          facetValueGroupConfig: {
            facetValueConfig: {
              facetValueLabelConfig: {
                type: "text",
              },
            },
          },
        },
      },
      sheetFirstLevel: {
        facetConfig: {
          facetValueGroupConfig: {
            type: "queueBox",
            facetValueConfig: {
              type: "chip",
              facetValueLabelConfig: {
                type: "text",
                showCount: false,
                size: "75",
              },
            },
          },
        },
      },
    },
  },
};

export { setOpenFilterIds, toggleFilterAccordion };

export function initializeConfigurableFacet(fragment: DocumentFragment) {
  /*                                                                                                       */
  setActiveConfiguration(isMobile() ? "sheetFirstLevel" : "default");
  mapElements(".ts_heureka_svelteFacet", (elem) => createFacet(elem, fragment), fragment)
    .filter(notEmpty)
    .map(CustomRangeInput.init);
}

/*                                                                 */
/*                                                                                        */
/*                                                                                              */
/*                                                        */
const mountedConfigurableFacets = new Map<string, ReturnType<typeof mount>>();

function createFacet(elem: HTMLElement, fragment?: DocumentFragment) {
  const parentElement = elem.parentElement as HTMLElement;
  const { serialized, facetVisibility, filterTitleMode, hasNbaValues } = elem.dataset;
  const facet: Facet = JSON.parse(serialized);
  if (isMobile() && facet.id.toLowerCase() === "retailer_otto") {
    /*                                                                 */
    const retailerOttoFilterTitle = FilterTitle.filterId(facet.filterId, fragment);
    const retailerOttoFilterAccordion = OcFilterAccordion.filterId(facet.filterId, fragment);
    retailerOttoFilterTitle?.remove();
    retailerOttoFilterAccordion?.remove();
    return;
  }
  const filterConfig =
    (facet.id.toLowerCase() === "retailprice" && config["singleSelectRange"]) ||
    (facet.selectionType === "range" &&
      (isActive("HEUREKA_2320_NO_HORIZONTAL_SCROLLING_FOR_DIMENSION_FACETS")
        ? config["interval"]
        : config["intervalDeprecated"])) ||
    (isDesktop() && facet.id.toLowerCase() === "retailer_otto" && config["ottoLogo"]) ||
    (facet.selectionType === "switch" && config["switch"]) ||
    (facet.selectionType === "radio" && config["radio"]) ||
    (facet.selectionType === "chip" && config["chip"]) ||
    (facet.selectionType === "rating" && config["rating"]) ||
    (facet.selectionType === "colorTile" && config["color"]) ||
    config["default"];

  const instance = mount(ConfigurableFacet, {
    target: parentElement,
    props: {
      data: {
        ...facet,
        showTitle: elem.dataset.showTitle === "true",
        useSlider: elem.dataset.mayHideSlider !== "true",
        parentId: `find_filter_${facet.filterId}`,
      },
      filterTitleMode: filterTitleMode,
      visibility: facetVisibility,
      hasNbaValues: hasNbaValues,
      ...filterConfig,
    },
  });
  mountedConfigurableFacets.set(facet.id, instance);
  elem.remove();
  return parentElement;
}

export function initializeNextBestActionComponent(
  componentType: Component<NextBestAction> | Component<NextBestActionDesktop>,
  refinementBar: HTMLElement | undefined,
  isDesktopNBA: boolean = false,
) {
  if (refinementBar) {
    if (isDesktopNBA) {
      /*                                                                                    */
      const refinementBarPlaceholder = document.getElementById("heureka_refinementBar__placeholder");
      if (
        refinementBarPlaceholder &&
        !refinementBarPlaceholder.classList.contains("heureka_refinementBar__placeholder--hidden")
      ) {
        refinementBarPlaceholder.classList.add("heureka_refinementBar__placeholder--hidden");
      }
    }
    const initializationClass = isDesktopNBA
      ? "ts_heureka_svelteDesktopNextBestAction"
      : "ts_heureka_svelteNextBestAction";
    const useNewModelForPriceRanges = getActiveVariation("e3474", "StatusQuo") !== "StatusQuo";

    mapElements(`.${initializationClass}`, (elem) => {
      const parentElement = elem.parentElement as HTMLElement;
      const { serialized } = elem.dataset;

      /*                                                         */
      const parsedData = JSON.parse(serialized);

      /*                                                                                                             */
      /*                                                                    */
      if (parsedData.values && !useNewModelForPriceRanges && isMobile()) {
        parsedData.values.forEach((value) => {
          if (value.name === "preis-in-eur") {
            value.referencedFacetValueId = "facetValue_retailprice_value";
          }
        });
      }
      mount(componentType, {
        target: refinementBar,
        props: {
          ...parsedData,
          containsOnlyNbaValues: elem.dataset.containsOnlyNbaValues === "true",
          withExpander: isDesktopNBA,
        },
      });
      elem.remove();
      return parentElement;
    });
  }
}

export function initializeFacetValueTags() {
  const initializationClass = "ts_heureka_svelteFacetValueTags";

  mapElements(`.${initializationClass}`, (elem) => {
    const parentElement = elem.parentElement as HTMLElement;
    const { serialized } = elem.dataset;

    /*                                                         */
    const parsedData = JSON.parse(serialized);
    mount(FacetValueTags, {
      target: parentElement,
      props: {
        ...parsedData,
      },
    });
    elem.remove();
    return parentElement;
  });
}

export function initializeFilterbarComponent(refinementBar: HTMLElement | undefined) {
  if (refinementBar) {
    const initializationClass = "ts_heureka_svelteNextBestAction";

    mapElements(`.${initializationClass}`, (elem) => {
      const parentElement = elem.parentElement as HTMLElement;
      const { serialized } = elem.dataset;

      /*                                                         */
      const parsedData = JSON.parse(serialized);

      mount(Filterbar, {
        target: refinementBar,
        props: {
          ...parsedData,
        },
      });
      elem.remove();
      return parentElement;
    });
  }
}

export function initializeQuickFilterHarmonizedComponent() {
  /*                              */
  if (document.getElementById("harmonized-filter-btn")) {
    return;
  }
  const cassandraPlaceholder = document.getElementById("cassandra-floating-btn");
  if (cassandraPlaceholder) {
    mount(FilterHarmonized, {
      target: cassandraPlaceholder, /*                                         */
      props: { hideAboveRefinementBar: true },
    });
  }
}

function initializeAssets() {
  mount(Assets, {
    target: document.body,
  });
}

export function initializeDesktopSortingComponent() {
  mapElements(".ts_heureka_svelteDesktopSorting", (elem) => {
    const parentElement = elem.parentElement as HTMLElement;
    const { serialized } = elem.dataset;

    mount(DesktopSorting, {
      target: parentElement,
      props: {
        ...JSON.parse(serialized),
      },
    });
    elem.remove();
    return parentElement;
  });
}

export function initializeFilterBannerComponent() {
  mapElements(".ts_heureka_svelteFilterBanner", (elem) => {
    const parentElement = elem.parentElement as HTMLElement;
    const { serialized } = elem.dataset;

    mount(FilterBanner, {
      target: parentElement,
      props: {
        ...JSON.parse(serialized),
      },
    });
    elem.remove();
    return parentElement;
  });
}

function initializeFacetGroups() {
  mapElements(".ts_heureka_svelteFacetGroupDropdown", (elem) => {
    const parentElement = elem.parentElement as HTMLElement;
    const { serialized } = elem.dataset;

    mount(FacetGroupDropdown, {
      target: parentElement,
      props: {
        ...JSON.parse(serialized),
        label: elem.dataset.label,
        name: elem.dataset.name,
        filterId: elem.dataset.filterId,
      },
    });
    elem.remove();
    return parentElement;
  });
}

export default function registerSvelteComponentInitialization() {
  if (isActive("HEUREKA_2167_MIGRATE_FILTER_BANNER_TO_SVELTE")) {
    events.onFilterSectionLoaded(initializeFilterBannerComponent);
  }
  if (isActive("HEUREKA_2167_MIGRATE_DESKTOP_SORTING_TO_SVELTE")) {
    events.onFilterSectionLoaded(initializeDesktopSortingComponent);
  }
  events.onFilterSectionLoaded(initializeFacetGroups);
  if (isMobile()) {
    events.onFilterSectionLoaded(() => {
      const refBar = refinementBar();
      clear(refBar);
      if (NextBestActionComponent.couldBeVisible()) {
        resetNBAScrollTracking();
      }
      if (getActiveVariation("e3911", "StatusQuo") === "Filterbar") {
        initializeFacetValueTags();
        initializeFilterbarComponent(refBar);
      } else {
        initializeNextBestActionComponent(NextBestAction, refBar);
      }
    });
    events.onFilterSectionLoaded(initializeQuickFilterHarmonizedComponent);
  } else {
    events.onFilterSectionLoaded(() => {
      const refBar = refinementBar();
      clear(refBar);
      initializeNextBestActionComponent(NextBestActionDesktop, refBar, true);
    });
  }
  eventQBus.once("heureka.filterSection.loaded", initializeAssets);
}
